import { useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ApiError } from '../api';
import { DialogLayer } from '../ui/Dialog';
import { trapTab } from '../ui/focus';

/**
 * Owner 2026-10-24 11:08–11:10 („musi mi wyświetlić że próbuję dodać wstępnie zarezerwowany sprzęt. jeśli wymuszę to musi
 * wskazać że trzeba dopożyczyć”): adding or raising gear that other events reserved first asks before it is saved, in the
 * packlist and in the quote alike. The server refuses such a write with 422 `reservation_conflict` and the list; this
 * asks „Dodaj mimo to” / „Anuluj” and sends it again with `confirmConflicts`. Shared by the packlist and the quote (one
 * dialog for a whole fill: every item in it).
 */

/**
 * `tentative` is absent for an event the person may not see (rule 7a): then just „zarezerwowane”. `block`: an equipment
 * block — the gear is out of the warehouse. `longer`: held beyond the asking event's days (owner 2026-10-24 15:35:
 * „Info o zajętości dłuższej na czerwono wyraźnie”).
 */
export type ConflictEvent = { eventId: string | null; name: string; firstDay: string; lastDay: string; quantity: number; tentative?: boolean; block?: true; longer?: true };
export type ReservationConflict = { itemId: string; itemName: string; quantity: number; missing: number; events: ConflictEvent[] };

/** The conflicts of a refused write, or null when the error is something else. */
export function reservationConflicts(error: unknown): ReservationConflict[] | null {
  if (!(error instanceof ApiError) || error.code !== 'reservation_conflict') return null;
  const list = (error.details as { conflicts?: unknown } | undefined)?.conflicts;
  return Array.isArray(list) ? (list as ReservationConflict[]) : [];
}

const dayText = (day: string) => `${Number(day.slice(8, 10))}.${day.slice(5, 7)}.${day.slice(0, 4)}`;
/** „7.11.2026” or „7.11.2026 – 8.11.2026”. */
export const daysText = (first: string, last: string) => (first === last ? dayText(first) : `${dayText(first)} – ${dayText(last)}`);

const shortDay = (day: string) => `${Number(day.slice(8, 10))}.${day.slice(5, 7)}`;

/**
 * One holder of the pieces: „6 szt. jest wstępnie zarezerwowane w „Wesele A” (7.11.2026 – 8.11.2026)”, or for a block
 * (owner 2026-10-24 15:35) „2 szt. — Blokada: Serwis (7.11–9.11) — nie ma w magazynie”.
 */
export function eventText(e: ConflictEvent): string {
  if (e.block) return `${e.quantity} szt. — ${e.name} (${e.firstDay === e.lastDay ? shortDay(e.firstDay) : `${shortDay(e.firstDay)}–${shortDay(e.lastDay)}`}) — nie ma w magazynie`;
  return `${e.quantity} szt. jest ${e.tentative === true ? 'wstępnie zarezerwowane' : 'zarezerwowane'} w „${e.name}” (${daysText(e.firstDay, e.lastDay)})`;
}

/** Shown in clear red: a block, or gear held longer than the asking event (owner 2026-10-24 15:35). */
export const isLongOccupation = (e: Pick<ConflictEvent, 'block' | 'longer'>) => e.block === true || e.longer === true;

/** „LR7: 6 szt. jest wstępnie zarezerwowane w „Wesele A” (7.11.2026 – 8.11.2026).” (several events: one after another). */
export function conflictText(c: ReservationConflict): string {
  return `${c.itemName}: ${c.events.map(eventText).join(', ')}.`;
}

let ask: ((conflicts: ReservationConflict[]) => Promise<boolean>) | null = null;

/** Asks with the app's dialog; without it (a test, a screen outside the layout) the write is not sent again. */
export function askAboutConflicts(conflicts: ReservationConflict[]): Promise<boolean> {
  return ask ? ask(conflicts) : Promise.resolve(false);
}

/**
 * Sends `write(false)`; refused for gear other events hold, asks, and on „Dodaj mimo to” sends `write(true)`. „Anuluj”:
 * null (nothing saved). Any other error is thrown as it was.
 */
export async function withConflictConfirm<T>(write: (confirmConflicts: boolean) => Promise<T>): Promise<T | null> {
  try {
    return await write(false);
  } catch (error) {
    const conflicts = reservationConflicts(error);
    if (!conflicts) throw error;
    if (!(await askAboutConflicts(conflicts))) return null;
    return write(true);
  }
}

/** The one dialog of the app (in `AppLayout`), over everything, centred. */
export function ReservationConflictHost() {
  const [pending, setPending] = useState<{ conflicts: ReservationConflict[]; answer: (yes: boolean) => void } | null>(null);
  useLayoutEffect(() => {
    ask = (conflicts) => new Promise<boolean>((resolve) => setPending({ conflicts, answer: resolve }));
    return () => {
      ask = null;
    };
  }, []);
  if (!pending) return null;
  const answer = (yes: boolean) => {
    setPending(null);
    pending.answer(yes);
  };
  return createPortal(
    <div className="dialog-backdrop">
      <div
        className="dialog panel"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="conflict-title"
        aria-describedby="conflict-text"
        data-feature-id="availability.conflicts"
        onKeyDown={(e) => {
          if (e.key !== 'Tab') return;
          trapTab(e.currentTarget, e.nativeEvent);
          e.stopPropagation();
        }}
      >
        <DialogLayer onClose={() => answer(false)} />
        <h2 id="conflict-title">Sprzęt zarezerwowany w innym wydarzeniu</h2>
        <div id="conflict-text">
          {pending.conflicts.map((c) => (
            <p key={c.itemId}>
              {c.itemName}:{' '}
              {c.events.map((e, i) => (
                <span key={i}>
                  {i > 0 && ', '}
                  <span className={isLongOccupation(e) ? 'busy-long' : undefined}>{eventText(e)}</span>
                </span>
              ))}
              .
            </p>
          ))}
          <p>Dodać mimo to? Brak trzeba będzie dopożyczyć.</p>
        </div>
        <div className="actions">
          <button className="btn primary" type="button" autoFocus onClick={() => answer(true)}>
            Dodaj mimo to
          </button>
          <button className="btn ghost" type="button" onClick={() => answer(false)}>
            Anuluj
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
